// Lightweight wrapper around the Credential Management API
// https://developer.mozilla.org/en-US/docs/Web/API/Credential_Management_API
// Gracefully degrades when the API isn't supported (Safari pre-15.4, Firefox).

const SUPPORTED = typeof navigator !== "undefined" && !!navigator.credentials;

// All federated providers we currently support, in order of preference.
export const FEDERATED_PROVIDERS = [
    "https://accounts.google.com",
    "https://www.facebook.com",
    "https://www.instagram.com",
    "https://www.linkedin.com",
];

const PROVIDER_TO_KEY = {
    "https://accounts.google.com": "google",
    "https://www.facebook.com": "facebook",
    "https://www.instagram.com": "instagram",
    "https://www.linkedin.com": "linkedin",
};

export function isCredentialsSupported() {
    return SUPPORTED;
}

/**
 * Store a password credential so the browser's password manager picks it up.
 * Call this AFTER a successful email/password login.
 */
export async function storePasswordCredential({ email, password, name, avatar }) {
    if (!SUPPORTED || typeof window.PasswordCredential !== "function") return;
    try {
        const cred = new window.PasswordCredential({
            id: email,
            password,
            name: name || email,
            iconURL: avatar || undefined,
        });
        await navigator.credentials.store(cred);
    } catch (e) {
        // Some browsers throw if the user dismissed the save prompt — that's fine.
        console.debug("storePasswordCredential skipped:", e?.message);
    }
}

/**
 * Store a federated credential (e.g. after a successful Google login) so the
 * next visit can offer a one-tap option for the same provider/account.
 */
export async function storeFederatedCredential({ email, name, avatar, provider }) {
    if (!SUPPORTED || typeof window.FederatedCredential !== "function" || !provider) return;
    try {
        const cred = new window.FederatedCredential({
            id: email,
            provider,
            name: name || email,
            iconURL: avatar || undefined,
        });
        await navigator.credentials.store(cred);
    } catch (e) {
        console.debug("storeFederatedCredential skipped:", e?.message);
    }
}

/**
 * Silently ask the browser for any saved credentials for this site.
 * mediation:
 *   - "silent"   — never show UI (returns null if user interaction would be required)
 *   - "optional" — show account chooser only if multiple options exist
 *   - "required" — always show the account chooser
 *
 * Returns either:
 *   { type: "password", id, password }
 *   { type: "federated", id, providerKey }   // providerKey = "google" | "facebook" | ...
 *   null
 */
export async function pickDeviceCredential({ mediation = "optional" } = {}) {
    if (!SUPPORTED) return null;
    try {
        const cred = await navigator.credentials.get({
            password: true,
            federated: { providers: FEDERATED_PROVIDERS },
            mediation,
        });
        if (!cred) return null;
        if (cred.type === "password") {
            return { type: "password", id: cred.id, password: cred.password };
        }
        if (cred.type === "federated") {
            const providerKey = PROVIDER_TO_KEY[cred.provider];
            if (!providerKey) return null;
            return { type: "federated", id: cred.id, providerKey };
        }
    } catch (e) {
        console.debug("pickDeviceCredential failed:", e?.message);
    }
    return null;
}

/**
 * Tell the browser the user explicitly signed out so it doesn't auto-sign-in
 * again on the next page load.
 */
export async function preventSilentAccess() {
    if (!SUPPORTED || !navigator.credentials.preventSilentAccess) return;
    try {
        await navigator.credentials.preventSilentAccess();
    } catch (e) { console.debug("preventSilentAccess failed:", e); }
}
